/* Aplica a paleta de cor da empresa no <html>.
 *
 * Duas origens, nesta ordem de confiança:
 *
 *   1. /user-current, depois do login — a paleta da empresa de quem entrou, já
 *      resolvida pela cadeia de representantes no servidor.
 *   2. /public/brand?host=..., antes de qualquer sessão — a paleta do dono do
 *      domínio que o visitante abriu. É o que faz a TELA DE LOGIN de um
 *      representante com domínio próprio já aparecer na cor dele.
 *
 * Nenhuma das duas chega a tempo da primeira pintura, então a última paleta
 * conhecida daquele host fica no localStorage e é aplicada antes do render. Sem
 * isso o painel piscaria azul a cada carregamento. A chave é por host de
 * propósito: a mesma máquina pode abrir o painel de dois representantes, e a
 * cor de um não deve vazar para o outro.
 *
 * O tema por domínio codificado em CSS (styles/white-label-restricar.css) tem
 * precedência sobre tudo: é um contrato de marca fechado, e a escolha de paleta
 * de um representante não deve conseguir repintar a Restricar.
 */

import { DEFAULT_PALETTE, isValidPalette } from "../config/themePalettes";
import { BASE_URL } from "../config/apiConfig";

const ATTRIBUTE = "data-theme-palette";

const root = () =>
  typeof document === "undefined" ? null : document.documentElement;

const currentHost = () =>
  typeof window === "undefined" ? "" : window.location.hostname;

/* Uma chave por host — ver o comentário do topo. */
const storageKey = (host = currentHost()) => `themePalette:${host}`;

/* Um white-label de domínio já pintou tudo — sair sem tocar em nada. */
const hasDomainTheme = () => !!root()?.getAttribute("data-white-label");

export const readStoredPalette = () => {
  try {
    const stored = localStorage.getItem(storageKey());
    return isValidPalette(stored) ? stored : DEFAULT_PALETTE;
  } catch (error) {
    /* Modo privado ou storage bloqueado: o padrão serve. */
    return DEFAULT_PALETTE;
  }
};

export const applyThemePalette = (palette) => {
  const element = root();
  if (!element || hasDomainTheme()) return null;

  const resolved = isValidPalette(palette) ? palette : DEFAULT_PALETTE;

  /* O azul é o :root de index.css, não um bloco próprio — para voltar a ele
     basta remover o atributo. Deixar data-theme-palette="azul" no <html> não
     quebraria nada, mas sugeriria um bloco de CSS que não existe. */
  if (resolved === DEFAULT_PALETTE) {
    element.removeAttribute(ATTRIBUTE);
  } else {
    element.setAttribute(ATTRIBUTE, resolved);
  }

  try {
    localStorage.setItem(storageKey(), resolved);
  } catch (error) {
    /* Não poder lembrar a escolha só custa um piscar de azul no próximo
       carregamento; não é motivo para derrubar a aplicação do tema. */
  }

  return resolved;
};

/* Chamada no boot, antes do render: pinta com a última paleta conhecida deste
   host. */
export const applyStoredThemePalette = () =>
  applyThemePalette(readStoredPalette());

/**
 * Busca a paleta do domínio atual e aplica. É o caminho da tela de login: sem
 * sessão, o servidor identifica a empresa pelo domínio (tabela
 * WhitelabelDomains) e devolve só a cor.
 *
 * Silenciosa por definição — API fora do ar, domínio não cadastrado ou rede
 * ruim mantêm o que já está pintado. Uma tela de login precisa abrir mesmo sem
 * conseguir descobrir a própria cor.
 */
export const syncThemePaletteFromHost = async () => {
  if (!root() || hasDomainTheme()) return null;

  const host = currentHost();
  if (!host || host === "localhost" || host === "127.0.0.1") return null;

  try {
    const response = await fetch(
      `${BASE_URL}/public/brand?host=${encodeURIComponent(host)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!response.ok) return null;

    const body = await response.json();
    const palette = body?.data?.themePalette;
    if (!isValidPalette(palette)) return null;

    return applyThemePalette(palette);
  } catch (error) {
    return null;
  }
};

/* Chamada ao sair da conta. Só esquece a paleta quando o host NÃO tem cor
   própria: num domínio white-label a cor é da marca, não da sessão, e apagá-la
   faria a tela de login piscar azul antes de o /public/brand responder. */
export const clearThemePalette = () => {
  const element = root();
  if (element && !hasDomainTheme()) element.removeAttribute(ATTRIBUTE);
  try {
    localStorage.removeItem(storageKey());
  } catch (error) {
    /* idem */
  }
};
